import { useState, useEffect, useMemo } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { Container, Row, Col, Form, Button, Alert, FloatingLabel } from "react-bootstrap"

const passwordRules = [
  { id: "length", label: "Minimálně 8 znaků", test: (value) => value.length >= 8 },
  { id: "lower", label: "Alespoň jedno malé písmeno", test: (value) => /[a-z]/.test(value) },
  { id: "upper", label: "Alespoň jedno velké písmeno", test: (value) => /[A-Z]/.test(value) },
  { id: "number", label: "Alespoň jedno číslo", test: (value) => /\d/.test(value) },
  { id: "symbol", label: "Alespoň jeden symbol", test: (value) => /[^A-Za-z0-9]/.test(value) },
]

const PasswordNew = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const [noveHeslo, setNoveHeslo] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)
  const [validated, setValidated] = useState(false)

  const checks = useMemo(() => {
    return passwordRules.map((rule) => ({ ...rule, ok: rule.test(noveHeslo) }))
  }, [noveHeslo])

  const isStrongPassword = checks.every((rule) => rule.ok)

  useEffect(() => {
    const timer = setTimeout(() => {
      setMessage(null)
      setError(null)
    }, 3000)

    return () => clearTimeout(timer)
  }, [error, message])

  const handleReset = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    const isFormValid = form.checkValidity() !== false

    setValidated(true)

    if (!isFormValid || !isStrongPassword) {
      e.stopPropagation()
      if (!isStrongPassword) {
        setError("Heslo nesplňuje bezpečnostní podmínky.")
      }
      return
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/login/reset-heslo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, noveHeslo }),
      })

      if (!response.ok) {
        const errorResponse = await response.json()
        throw new Error(errorResponse.message)
      }

      setMessage("Heslo bylo úspěšně změněno.")
      setTimeout(() => navigate("/login"), 2000)
    } catch (requestError) {
      setError(requestError.message)
    }
  }

  return (
    <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-primary px-5">
      <div className="login-container shadow-lg rounded-4 overflow-hidden mx-sm-5">
        <Row className="g-0">
          <Col md={6} className="p-5 bg-white order-2 order-md-1">
            <Form noValidate validated={validated} onSubmit={handleReset} className="h-100 d-flex flex-column justify-content-center">
              <h3 className="mb-4">Nové heslo</h3>

              <Form.Group className="mb-2" controlId="formUzivatel">
                <div className="input-group has-validation password-input-group">
                  <FloatingLabel controlId="noveheslo" label="Heslo" className="flex-grow-1">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      className={`rounded-start-pill ${validated && !isStrongPassword ? "is-invalid" : ""}`}
                      placeholder="Heslo"
                      minLength={8}
                      maxLength={64}
                      value={noveHeslo}
                      onChange={(e) => {
                        setNoveHeslo(e.target.value)
                        if (error) setError(null)
                      }}
                      required
                    />
                  </FloatingLabel>
                  <button
                    type="button"
                    className="btn btn-outline-secondary password-toggle-group-btn rounded-end-pill"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Skrýt heslo" : "Zobrazit heslo"}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </button>
                  <div className="invalid-feedback">Nové heslo musí splňovat všechny podmínky níže.</div>
                </div>
              </Form.Group>

              <div className="password-checklist mb-4">
                {checks.map((rule) => (
                  <div key={rule.id} className={`password-check-item ${rule.ok ? "is-ok" : "is-pending"}`}>
                    <i className={`bi ${rule.ok ? "bi-check-circle-fill" : "bi-x-circle"}`}></i>
                    <span>{rule.label}</span>
                  </div>
                ))}
              </div>

              <div className="mb-4">
                <Link to="/login" className="text-muted small">Zpět k přihlášení</Link>
              </div>

              <Button type="submit" size="lg" className="w-100 rounded-pill py-2">
                Uložit
              </Button>

              {message && (
                <Alert variant="success" className="mt-3 rounded-pill py-2">
                  <i className="bi bi-check2 pe-2"></i>
                  {message}
                </Alert>
              )}

              {error && (
                <Alert variant="danger" className="mt-3 rounded-pill py-2">
                  <i className="bi bi-exclamation-triangle pe-2"></i>
                  {error}
                </Alert>
              )}
            </Form>
          </Col>

          <Col md={6} className="bg-primary text-white d-flex flex-column justify-content-center align-items-center p-4 position-relative order-1 order-md-2">
            <svg
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{ opacity: 0.1, zIndex: 0 }}
              viewBox="1700 0 17000 17000"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8469.39 4560.02v3344.05l1836.83-1081.97 1836.83 1081.97V4560.02l-1836.83-1081.96-1836.83 1081.96zm1836.83 9195.12h1836.83v-500.87l-1836.83-1081.97-1836.83 1081.97v500.87h1836.83zm1836.83-4848v3344.05l-1836.83-1081.96-1836.83 1081.96V8907.14l1836.83-1081.96 1836.83 1081.96zM7617.87 8405.66V5061.62L5202.73 6484.23l-.03 3344.04zM7617.87 12752.78V9408.74l-2415.14 1422.61v1767.97c0 513.88 339.93 952.19 805.98 1101.32l1609.16-947.86zM6358.55 3478.06h1259.32v580.48L5202.73 5481.12v-847.24c0-635.7 520.12-1155.82 1155.82-1155.82zM12994.57 8405.66V5061.62l2415.14 1422.61.03 3344.04zM12994.57 12752.78V9408.74l2415.14 1422.61v1767.97c0 513.88-339.93 952.19-805.98 1101.32l-1609.16-947.86zM14253.89 3478.06h-1259.32v580.48l2415.14 1422.58v-847.24c0-635.7-520.12-1155.82-1155.82-1155.82z"/>
            </svg>
            <div style={{ zIndex: 1 }} className="text-center">
              <i className="bi bi-journal-text display-3"></i>
              <h3 className="mt-3">Uživatelský přístup</h3>
              <p>Zvolte si nové přístupové heslo podle bezpečnostních pravidel.</p>
            </div>
          </Col>
        </Row>
      </div>
    </Container>
  )
}

export default PasswordNew

